// src/lib/goalCapability.ts
//
// BekalNanti — Milestone P3.5: Goal Type Capability Registry
// Centralizes domain capabilities, modules readiness, and person-goal association logic.
// Strictly decouples Education vertical slice (P3, P4, P5) from non-education goals.

export interface GoalCapability {
  goalType: string
  title: string
  englishLabel: string
  supportsTargetSpecification: boolean
  supportsCostFunding: boolean
  supportsCalculation: boolean
  statusDescription: string
}

export const GOAL_CAPABILITIES: Record<string, GoalCapability> = {
  education: {
    goalType: 'education',
    title: 'Pendidikan',
    englishLabel: 'Education',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Modul perencanaan aktif (P3 Sasaran, P4 Biaya, P5 Kalkulasi).',
  },
  career: {
    goalType: 'career',
    title: 'Karier & Kerja',
    englishLabel: 'Career',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: pemetaan target karier, biaya persiapan, dan alokasi modal.',
  },
  housing: {
    goalType: 'housing',
    title: 'Rumah & Tempat Tinggal',
    englishLabel: 'Housing',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: pemetaan tipe hunian, DP KPR/tunai, pajak, dan sumber dana.',
  },
  financial: {
    goalType: 'financial',
    title: 'Kemandirian Finansial',
    englishLabel: 'Financial Independence',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: akumulasi aset produktif dan ketahanan arus kas keluarga.',
  },
  emergency: {
    goalType: 'emergency',
    title: 'Dana Darurat & Keamanan',
    englishLabel: 'Emergency Fund',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: target ketahanan 3-12 bulan pengeluaran dan pemulihan cadangan.',
  },
  family: {
    goalType: 'family',
    title: 'Keluarga & Pernikahan',
    englishLabel: 'Family & Wedding',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: persiapan akad, resepsi, busana, katering, dan bekal rumah tangga.',
  },
  parents: {
    goalType: 'parents',
    title: 'Membantu Orang Tua',
    englishLabel: 'Parents',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: alokasi nafkah rutin dan jaminan kesehatan orang tua.',
  },
  business: {
    goalType: 'business',
    title: 'Usaha & Bisnis',
    englishLabel: 'Business',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: modal awal rintisan, legalitas, peralatan, dan modal kerja.',
  },
  retirement: {
    goalType: 'retirement',
    title: 'Pensiun',
    englishLabel: 'Retirement',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: horizon usia pensiun, biaya hidup hari tua, dan kesehatan.',
  },
  vehicle: {
    goalType: 'vehicle',
    title: 'Kendaraan Keluarga',
    englishLabel: 'Vehicle',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: target kendaraan, harga beli/DP, asuransi, dan pajak tahunan.',
  },
  hajj_umrah: {
    goalType: 'hajj_umrah',
    title: 'Haji & Umrah',
    englishLabel: 'Hajj & Umrah',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: paket ibadah, tiket, dam, uang saku, dan masa tunggu.',
  },
  wedding: {
    goalType: 'wedding',
    title: 'Pernikahan',
    englishLabel: 'Wedding',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: skala acara, katering, tempat, dokumentasi, dan mahar.',
  },
  vacation: {
    goalType: 'vacation',
    title: 'Liburan & Wisata',
    englishLabel: 'Vacation',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: tiket penerbangan, akomodasi, atraksi, dan visa wisata.',
  },
  notsure: {
    goalType: 'notsure',
    title: 'Lainnya / Sasaran Khusus',
    englishLabel: 'Other / Custom Goal',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: perumusan sasaran mandiri, estimasi alokasi, dan target waktu.',
  },
}

/**
 * Checks whether a given goal type is an Education goal.
 */
export function isEducationGoal(goalType?: string | null): boolean {
  return (goalType || '').trim().toLowerCase() === 'education'
}

/**
 * Retrieves the capability descriptor for any goal type, with a safe fallback.
 */
export function getGoalCapability(goalType?: string | null): GoalCapability {
  const normalized = (goalType || '').trim().toLowerCase()
  if (normalized && GOAL_CAPABILITIES[normalized]) {
    return GOAL_CAPABILITIES[normalized]
  }
  return {
    goalType: normalized || 'unknown',
    title: normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Umum',
    englishLabel: 'General Goal',
    supportsTargetSpecification: true,
    supportsCostFunding: true,
    supportsCalculation: true,
    statusDescription: 'Perencanaan aktif: pemetaan kebutuhan sasaran, biaya, dan sumber dana keluarga.',
  }
}

/**
 * Human-readable Indonesian label for relationships.
 */
export const RELATIONSHIP_LABELS: Record<string, string> = {
  self: 'Saya',
  spouse: 'Pasangan',
  child: 'Anak',
  dependent: 'Tanggungan',
  family: 'Keluarga',
  household: 'Anggota Lain',
}

export function formatRelationshipLabel(relationship?: string | null): string {
  if (!relationship) return 'Keluarga'
  return RELATIONSHIP_LABELS[relationship.toLowerCase()] || relationship
}

/**
 * Resolves person ownership details for display.
 */
export function getGoalOwnerDisplay(
  goal: { personId?: string | null },
  persons: Array<{ id: string; fullName: string; relationship: string; status?: string }>,
  archivedPersons?: Array<{ id: string; fullName: string; relationship: string; status?: string }>
): {
  name: string
  relationshipLabel: string
  isHousehold: boolean
  isArchived: boolean
  fullNameWithRelation: string
} {
  if (!goal.personId) {
    return {
      name: 'Keluarga',
      relationshipLabel: 'Bersama',
      isHousehold: true,
      isArchived: false,
      fullNameWithRelation: 'Keluarga (Bersama)',
    }
  }

  const person = persons.find((p) => p.id === goal.personId)
  if (person) {
    const isArchived = person.status === 'archived'
    const relLabel = formatRelationshipLabel(person.relationship)
    return {
      name: person.fullName,
      relationshipLabel: isArchived ? `${relLabel} (Diarsipkan)` : relLabel,
      isHousehold: false,
      isArchived,
      fullNameWithRelation: isArchived
        ? `${person.fullName} (${relLabel} - Diarsipkan)`
        : `${person.fullName} (${relLabel})`,
    }
  }

  if (archivedPersons && archivedPersons.length > 0) {
    const archivedPerson = archivedPersons.find((p) => p.id === goal.personId)
    if (archivedPerson) {
      const relLabel = formatRelationshipLabel(archivedPerson.relationship)
      return {
        name: archivedPerson.fullName,
        relationshipLabel: `${relLabel} (Diarsipkan)`,
        isHousehold: false,
        isArchived: true,
        fullNameWithRelation: `${archivedPerson.fullName} (${relLabel} - Diarsipkan)`,
      }
    }
  }

  return {
    name: 'Keluarga',
    relationshipLabel: 'Bersama',
    isHousehold: true,
    isArchived: false,
    fullNameWithRelation: 'Keluarga',
  }
}

/**
 * Deterministically resolves which person should own a newly selected goal
 * based on the goal type and the household's mapped relationships.
 *
 * Rules:
 * - education -> child if present, else self, else dependent, else null
 * - retirement -> self if present, else spouse, else null
 * - career / business -> self if present, else null
 * - emergency / housing / financial -> self if present, else null
 * - parents -> dependent if present, else family, else self, else null
 * - family / notsure / others -> self if present, else null
 */
export function resolveDeterministicPersonId(
  goalType: string,
  personIdByRelationship: Record<string, string>
): string | null {
  const norm = (goalType || '').trim().toLowerCase()

  if (norm === 'education') {
    return (
      personIdByRelationship['child'] ||
      personIdByRelationship['self'] ||
      personIdByRelationship['dependent'] ||
      null
    )
  }

  if (norm === 'retirement') {
    return (
      personIdByRelationship['self'] ||
      personIdByRelationship['spouse'] ||
      null
    )
  }

  if (norm === 'career' || norm === 'business') {
    return personIdByRelationship['self'] || null
  }

  if (norm === 'parents') {
    return (
      personIdByRelationship['dependent'] ||
      personIdByRelationship['family'] ||
      personIdByRelationship['self'] ||
      null
    )
  }

  // emergency, housing, financial, family, notsure, default
  return personIdByRelationship['self'] || null
}
